import React, { useState, useRef, useEffect, useMemo } from 'react';
import './SearchableDropdown.css';

/**
 * Searchable dropdown with optional multi-select, grouping, and icons.
 *
 * Props:
 *   items        – array of objects
 *   getId        – (item) => unique id
 *   getLabel     – (item) => display string
 *   getGroup     – (item) => group label (optional, for grouped lists)
 *   renderIcon   – (item) => ReactNode (optional, icon before label)
 *   placeholder  – input placeholder
 *
 * Single-select:
 *   value        – selected id (string | null)
 *   onChange     – (id) => void
 *   allowClear   – show "None" / clear option (default true)
 *
 * Multi-select:
 *   multi        – true
 *   selected     – array of selected items
 *   onAdd        – (item) => void
 *   onRemove     – (id) => void
 */
export default function SearchableDropdown({
  items = [],
  getId = (i) => i.id,
  getLabel = (i) => i.name,
  getGroup,
  renderIcon,
  placeholder = 'Search…',

  // single
  value,
  onChange,
  allowClear = true,

  // multi
  multi = false,
  selected = [],
  onAdd,
  onRemove,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Filter
  const selectedIds = multi ? new Set(selected.map(getId)) : new Set();
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = items;
    if (multi) list = list.filter((i) => !selectedIds.has(getId(i)));
    if (q) list = list.filter((i) => getLabel(i).toLowerCase().includes(q));
    return list;
  }, [items, search, selectedIds, multi, getId, getLabel]);

  // Group
  const groups = useMemo(() => {
    if (!getGroup) return null;
    const map = new Map();
    for (const item of filtered) {
      const g = getGroup(item) || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(item);
    }
    return map;
  }, [filtered, getGroup]);

  const handleSelect = (item) => {
    if (multi) {
      onAdd?.(item);
      setSearch('');
      inputRef.current?.focus();
    } else {
      onChange?.(getId(item));
      setOpen(false);
      setSearch('');
    }
  };

  const handleClear = () => {
    onChange?.(null);
    setOpen(false);
    setSearch('');
  };

  // Display value for single-select
  const selectedItem = !multi && value ? items.find((i) => getId(i) === value) : null;

  const renderList = (list) =>
    list.map((item) => (
      <button
        key={getId(item)}
        type="button"
        className={`sd-option ${!multi && getId(item) === value ? 'sd-option--active' : ''}`}
        onClick={() => handleSelect(item)}
      >
        {renderIcon && <span className="sd-option__icon">{renderIcon(item)}</span>}
        <span className="sd-option__label">{getLabel(item)}</span>
      </button>
    ));

  return (
    <div className="sd-container" ref={containerRef}>
      {/* Multi chips */}
      {multi && selected.length > 0 && (
        <div className="sd-chips">
          {selected.map((item) => (
            <span key={getId(item)} className="sd-chip">
              {renderIcon && <span className="sd-chip__icon">{renderIcon(item)}</span>}
              {getLabel(item)}
              <button type="button" onClick={() => onRemove?.(getId(item))} className="sd-chip__x">
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="sd-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="sd-input"
          placeholder={selectedItem ? getLabel(selectedItem) : placeholder}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {!multi && selectedItem && (
          <button type="button" className="sd-clear" onClick={handleClear} title="Clear">
            ×
          </button>
        )}
        <span className="sd-chevron">{open ? '▴' : '▾'}</span>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="sd-dropdown">
          {!multi && allowClear && (
            <button
              type="button"
              className={`sd-option sd-option--none ${!value ? 'sd-option--active' : ''}`}
              onClick={handleClear}
            >
              <span className="sd-option__label" style={{ color: 'var(--text-muted)' }}>
                None
              </span>
            </button>
          )}

          {filtered.length === 0 ? (
            <div className="sd-empty">No matches</div>
          ) : groups ? (
            [...groups.entries()].map(([group, list]) => (
              <div key={group}>
                <div className="sd-group-label">{group}</div>
                {renderList(list)}
              </div>
            ))
          ) : (
            renderList(filtered)
          )}
        </div>
      )}
    </div>
  );
}
